// routes/TasksPane.jsx — Task 판 (v5 §12 시안 4a·4b).
//
// **lane 은 명령이 아니라 표현이다.** 서버(task-liveness)가 준 counts·awaitingMe·handoffsToMe
// 를 네 lane 으로 배치해 보여줄 뿐, 카드 이동도 상태 변경도 없다 — `task.transition` 은
// 어휘에 존재하지 않는다(A-7). Context Pack N+1 도 여전히 금지다.
//
// 시안에서 옮기지 못한 것(서버가 주지 않는 값 — 지어내지 않는다):
//  · 카드의 참여자 아바타 · 「박서준 → 나」 같은 상대 이름 — task-board 에 이름이 없다
//  · 「마지막 판단」 열 — 그런 시각이 읽기에 없다
import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { read } from '../api/client.js';
import { useResource } from '../api/resource.js';
import { useSession } from '../session/session.jsx';
import { Button, Chip, EmberGlyph, Icon } from '../ui/primitives.jsx';
import { Empty, Resource } from '../ui/states.jsx';
import './tasks.css';
import './task-reentry.css';

// 칸반의 열은 작업을 "이동"시키는 명령이 아니다. 서버가 계산한 liveness를 사람이
// 한눈에 다시 읽을 수 있게 세 장면으로 묶은 표현이다.
const BOARD_LANES = [
  { key: 'now', label: '지금 필요', note: '판단 · 응답', includes: new Set(['decide', 'respond']) },
  { key: 'watch', label: '함께 읽는 중', note: '제안 · 질문', includes: new Set(['watch']) },
  { key: 'quiet', label: '잠시 멈춤', note: '다시 열 수 있음', includes: new Set(['dormant']) },
];

const FILTERS = [
  ['mine', '내 책임'],
  ['all', '전체'],
  ['open', '열린 판단만'],
];

export function TasksPane({ spaceId }) {
  const session = useSession();
  const [view, setView] = useState('board');
  const [filter, setFilter] = useState('all');
  const [afterCursor, setAfterCursor] = useState(null);
  const [seen, setSeen] = useState([]);
  const board = useResource(`space:${spaceId}:tasks:${afterCursor ?? 'first'}`, () =>
    read('task-board', { nodeId: session.nodeId, query: { spaceId, limit: 100, afterCursor } }),
  );
  useEffect(() => {
    if (board.status !== 'ready') return;
    setSeen((prior) => afterCursor === null ? (board.data.tasks ?? []) : [...prior, ...(board.data.tasks ?? [])]);
  }, [board.status, board.data, afterCursor]);
  const taskIds = seen.map((task) => task.taskId).join(',');
  const liveness = useResource(taskIds ? `space:${spaceId}:tasks:liveness:${taskIds}` : null, () =>
    read('task-liveness', { nodeId: session.nodeId, query: { spaceId, taskIds } }),
  );

  return <section class="work work--fluid work--tasks">
    <header class="work__head">
      <div class="work__head-text">
        <h1 class="t-view">Task</h1>
        <p class="t-meta">대화에서 생긴 판단을 현재 흐름별로 읽습니다.</p>
      </div>
      <div class="tasks-controls">
        <div class="tasks-view-toggle" role="group" aria-label="보기 전환">
          <button type="button" class="tseg__item" aria-pressed={view === 'board'} onClick={() => setView('board')}>보드</button>
          <button type="button" class="tseg__item" aria-pressed={view === 'list'} onClick={() => setView('list')}>목록</button>
        </div>
        <div class="tasks-filters" role="group" aria-label="필터">
          {FILTERS.map(([key, label]) => (
            <button type="button" key={key} class="fpill" aria-pressed={filter === key} onClick={() => setFilter(key)}>{label}</button>
          ))}
        </div>
      </div>
    </header>
    <div class="work__body">
      <Resource resource={board} loadingLabel="Task를 읽는 중">{() => {
        if (seen.length === 0 && afterCursor === null) {
          return view === 'board'
            ? <EmptyBoard />
            : <Empty title="아직 이어갈 판단이 없습니다.">대화에서 판단이 필요해지면 그 자리에서 Task를 만듭니다.</Empty>;
        }
        return <Resource resource={liveness} loadingLabel="불씨 요약을 읽는 중">{(data) => {
          const rows = decorate(seen, data.tasks ?? []).filter((row) => matches(row, filter));
          if (rows.length === 0) {
            return view === 'board'
              ? <EmptyBoard filtered />
              : <Empty title="이 필터에 잡히는 Task가 없습니다.">필터를 바꾸면 나머지가 보입니다.</Empty>;
          }
          return view === 'board' ? <Board rows={rows} spaceId={spaceId} /> : <TableList rows={rows} spaceId={spaceId} />;
        }}</Resource>;
      }}</Resource>
      {board.status === 'ready' && board.data.nextCursor ? (
        <div class="task-more">
          <p class="t-meta">지금까지 읽은 {seen.length}건을 배치했습니다. 더 있습니다.</p>
          <Button size="sm" onClick={() => setAfterCursor(board.data.nextCursor)}>더 보기</Button>
        </div>
      ) : null}
    </div>
  </section>;
}

/** 서버 요약 → 화면 행. lane·대표 미결은 서버 값의 우선순위 배치일 뿐 새 판정이 아니다. */
function decorate(tasks, summaries) {
  const byId = new Map(summaries.map((summary) => [summary.taskId, summary]));
  return tasks.flatMap((task) => {
    const summary = byId.get(task.taskId);
    if (!summary) return [];
    const waitingProposals = summary.awaitingMe?.proposals ?? 0;
    const waitingQuestions = summary.awaitingMe?.questions ?? 0;
    const offered = summary.handoffsToMe?.offered ?? 0;
    const accepted = summary.handoffsToMe?.accepted ?? 0;
    const ember = (summary.counts?.questions ?? 0) + (summary.counts?.handoffs ?? 0) + (summary.counts?.proposals ?? 0);
    const lane = waitingProposals > 0 ? 'decide'
      : (waitingQuestions > 0 || offered > 0 || accepted > 0) ? 'respond'
      : ember > 0 ? 'watch' : 'dormant';
    const dominant = lane === 'decide' ? { tone: 'human', label: '제안 · 수락 가능' }
      : waitingQuestions > 0 ? { tone: 'waiting', label: `질문 ${waitingQuestions} · 나에게` }
      : offered > 0 ? { tone: 'handoff', label: '넘김 · 수락 대기' }
      : accepted > 0 ? { tone: 'handoff', label: '넘김 · 진행' }
      : lane === 'watch' ? { tone: 'handoff', label: watchLabel(summary.counts) }
      : null;
    return [{ task, summary, lane, dominant, ember, mine: waitingProposals + waitingQuestions + offered + accepted > 0 }];
  });
}

function watchLabel(counts) {
  if (counts?.proposals) return `제안 ${counts.proposals} · 검토 중`;
  if (counts?.questions) return `질문 ${counts.questions} · 다른 참여자에게`;
  return `넘김 ${counts?.handoffs ?? 0} · 진행`;
}

function matches(row, filter) {
  if (filter === 'mine') return row.mine || row.task.viewerRole === 'owner';
  if (filter === 'open') return row.ember > 0;
  return true;
}

function Board({ rows, spaceId }) {
  return <div class="task-board">
    {BOARD_LANES.map(({ key, label, note, includes }) => {
      const cards = rows.filter((row) => includes.has(row.lane));
      return <BoardLane key={key} lane={key} label={label} note={note} cards={cards} spaceId={spaceId} />;
    })}
  </div>;
}

function EmptyBoard({ filtered = false }) {
  return <div class="task-board task-board--empty">
    {BOARD_LANES.map(({ key, label, note }, index) => (
      <section class={`board-lane board-lane--${key}`} key={key} aria-label={label}>
        <header class="board-lane__head">
          <span class="board-lane__dot" aria-hidden="true" />
          <span class="board-lane__label">{label}</span>
          <span class="board-lane__note">{note}</span>
        </header>
        {index === 0 ? <div class="board-empty-card">
          <Icon name="task" />
          <p>{filtered ? '선택한 범위에 Task가 없습니다.' : '아직 이어갈 판단이 없습니다.'}</p>
          <span>대화에서 판단이 필요해지면 여기에 나타납니다.</span>
        </div> : <div class="board-lane__quiet" aria-hidden="true" />}
      </section>
    ))}
  </div>;
}

function BoardLane({ lane, label, note, cards, spaceId }) {
  return <section class={`board-lane board-lane--${lane}`} aria-label={`${label} ${cards.length}건`}>
    <header class="board-lane__head">
      <span class="board-lane__dot" aria-hidden="true" />
      <span class="board-lane__label">{label}</span>
      <span class="board-lane__count t-num">{cards.length}</span>
      <span class="board-lane__note">{note}</span>
    </header>
    <div class="board-lane__cards">
      {cards.map(({ task, lane: rowLane, dominant, ember }) => (
        <div class="task-reentry-card" key={task.taskId}>
        <Link href={`/s/${spaceId}/task/${task.taskId}`} class={`task-card task-card--${rowLane}`}>
          <span class="task-card__title">{task.title}</span>
          <span class="task-card__footer">
            {dominant ? <Chip tone={dominant.tone}>{dominant.label}</Chip> : <span class="task-card__quiet"><Icon name="dormant" /> 잠시 멈춤</span>}
            <EmberGlyph count={ember} />
          </span>
          <span class="task-card__context">
            {task.currentVersionNo ? `공동 이해 v${task.currentVersionNo}` : '원문 근거 연결됨'}
            {task.viewerRole === 'owner' ? ' · 내 책임' : ''}
          </span>
        </Link>
        <Link class="task-reentry-link t-meta" href={`/s/${spaceId}/task/${task.taskId}/reentry`} aria-label={`${task.title} · 맥락부터 읽기`}>맥락부터 읽기</Link>
        </div>
      ))}
      {cards.length === 0 ? <p class="board-lane__empty">비어 있습니다</p> : null}
    </div>
  </section>;
}

function TableList({ rows, spaceId }) {
  return <section class="tlist" aria-label="Task 목록">
    <div class="tlist__head t-meta">
      <span>Task</span><span>대표 미결</span><span>불씨</span><span>공동 이해</span><span>책임</span>
    </div>
    {rows.map(({ task, lane, dominant, ember }) => (
      <div class="task-reentry-entry" key={task.taskId}>
      <Link href={`/s/${spaceId}/task/${task.taskId}`} class="tlist__row">
        {lane === 'decide' || lane === 'respond' ? <span class={`tlist__mark tlist__mark--${lane}`} aria-hidden="true" /> : null}
        <span class={`tlist__title t-body${lane === 'dormant' ? ' tlist__title--dormant' : ''}`}>{task.title}</span>
        {dominant
          ? <span class="tlist__dominant"><Chip tone={dominant.tone}>{dominant.label}</Chip></span>
          : <span class="tlist__dominant tlist__cell row"><Icon name="dormant" /> 휴면</span>}
        <span class="tlist__ember tlist__cell">{ember > 0 ? <EmberGlyph count={ember} /> : <span class="tlist__absent">—</span>}</span>
        <span class="tlist__version tlist__cell t-mono">{task.currentVersionNo ? `v${task.currentVersionNo}` : <span class="tlist__absent">—</span>}</span>
        <span class="tlist__role tlist__cell">{task.viewerRole === 'owner' ? '나' : roleLabel(task.viewerRole)}</span>
      </Link>
      <Link class="task-reentry-link t-meta" href={`/s/${spaceId}/task/${task.taskId}/reentry`} aria-label={`${task.title} · 맥락부터 읽기`}>맥락부터 읽기</Link>
      </div>
    ))}
  </section>;
}

const roleLabel = (role) => ({ participant: '참여자', observer: '관찰자', none: '—' })[role] ?? role;
